import { useEffect, useState } from 'react';

import { send } from 'loot-core/platform/client/fetch';
import { integerToAmount } from 'loot-core/shared/util';
import { format as monthUtilFormat } from 'loot-core/src/shared/months';

import { useAuth } from '../../../../hooks/useAuth';
import { useDateFormat } from '../../../../hooks/useDateFormat';
import { theme } from '../../../../style';
import { gray300, gray400, gray500 } from '../../../../style/palette';
import { gsGreen700 } from '../../../../style/themes/light';
import { Link } from '../../../common/Link';

import styles from './billingInfo.module.scss';

type BillingInfoProps = {
  showGoToBillingLinks?: boolean;
};

const BillingInfo: React.FC<BillingInfoProps> = ({ showGoToBillingLinks }) => {
  const { billingStatus } = useAuth();
  const [subscriptionPlan, setSubscriptionPlan] = useState<{ name?: string } | null>(null);
  const dateFormat = useDateFormat() || 'MM/dd/yyyy';

  useEffect(() => {
    if (billingStatus) {
      getSubscriptionPlan();
    }
  }, [billingStatus]);

  const getSubscriptionPlan = async () => {
    const products = await send('stripe-get-products');
    const currentPlan = products.find(
      p => p.productId === billingStatus.subscriptionPlan,
    );
    setSubscriptionPlan(currentPlan);
  };

  const getBillingInfoView = () => {
    const gotToBillingLink = (
      <Link to="/billing" variant="internal" style={{ color: gray400 }}>
        go to Billing
      </Link>
    );
    const nextPaymentText = !billingStatus?.cancellationRequested &&
      billingStatus?.upcomingPaymentDate &&
      billingStatus?.upcomingPaymentAmount &&
      !billingStatus?.isFreeTrialProduct && (
        <span style={{ fontSize: 13 }}>
          Your next payment is scheduled for{' '}
          <strong>
            {monthUtilFormat(billingStatus.upcomingPaymentDate, dateFormat)}
          </strong>
          . The due amount is{' '}
          <strong>
            $
            {billingStatus.upcomingPaymentAmount
              ? integerToAmount(billingStatus.upcomingPaymentAmount)
              : 0}
          </strong>
          .
        </span>
      );
    const lastPaymentText = billingStatus.stripeStatus !== 'trialing' &&
      billingStatus.latestPaymentDate && (
        <span style={{ fontSize: 13 }}>
          Your last payment of $
          {billingStatus.latestPaymentAmount
            ? integerToAmount(billingStatus.latestPaymentAmount)
            : 0}{' '}
          was at {monthUtilFormat(billingStatus.latestPaymentDate, dateFormat)}.
        </span>
      );
    const financialInstitutionsUsageText = (
      <span style={{ fontSize: 14, color: gsGreen700 }}>
        You have linked {billingStatus.currentUsage} of available{' '}
        {billingStatus.maxInstitutions} financial institutions.
      </span>
    );
    const freeVersionView = (
      <>
        <span style={{ fontSize: '17px' }}>You are on a free version</span>
        <span className="secondary-text" style={{ color: gray300 }}>
          In order to link your bank accounts and sync devices subscribe to one
          of the available plans.
        </span>
        {showGoToBillingLinks && (
          <span className="secondary-text" style={{ color: gray300 }}>
            To learn more details {gotToBillingLink}.
          </span>
        )}
      </>
    );
    const subsciptionPlanView = (
      <div className="flex-column">
        <span
          style={{
            textAlign: 'center',
            color: gray300,
            lineHeight: '18px',
          }}
        >
          Your subscription plan is
        </span>
        <span
          style={{
            fontSize: '17px',
            color: gsGreen700,
            lineHeight: '24px',
          }}
        >
          <strong>{subscriptionPlan ? subscriptionPlan.name : 'Unknown'}</strong>
        </span>
      </div>
    );
    const paymentMethodText = billingStatus?.cardBrand &&
      billingStatus?.cardLastFourDigits && (
        <span>
          Your payment method is{' '}
          <span style={{ textTransform: 'capitalize' }}>
            <strong>{`${billingStatus?.cardBrand} ••••`}</strong>
            <strong>{billingStatus?.cardLastFourDigits}</strong>
          </span>
        </span>
      );
    const subscriptionWillBeCancelledText =
      billingStatus?.cancellationRequested && (
        <span style={{ fontSize: 13, color: theme.warningTextLight }}>
          Your subscription will be canceled at{' '}
          <strong>
            {monthUtilFormat(
              new Date(billingStatus?.cancelAt * 1000),
              dateFormat,
            )}
          </strong>
        </span>
      );
    const calculateTimeLeft = (trialEnds: string | Date) => {
      const now = new Date();
      const end = new Date(trialEnds);
      const diffMs = end.getTime() - now.getTime();

      if (diffMs <= 0) {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }

      const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

      return { days, hours, minutes, seconds };
    };

    const getCountdownText = (timeLeft: ReturnType<typeof calculateTimeLeft>) => {
      if (timeLeft.days > 1) {
        return `${timeLeft.days} days left`;
      } else if (timeLeft.days === 1 || timeLeft.hours > 0) {
        return `${timeLeft.hours} hour${timeLeft.hours !== 1 ? 's' : ''} ${timeLeft.minutes} minute${timeLeft.minutes !== 1 ? 's' : ''} left`;
      } else if (timeLeft.minutes > 0) {
        return `${timeLeft.minutes} minute${timeLeft.minutes !== 1 ? 's' : ''} left`;
      } else {
        return `${timeLeft.seconds} second${timeLeft.seconds !== 1 ? 's' : ''} left`;
      }
    };

    switch (billingStatus?.stripeStatus) {
      case 'trialing':
        const timeLeft = calculateTimeLeft(billingStatus.trialEnds);
        const countdownText = getCountdownText(timeLeft);
        return (
          <>
            <div className="flex-column">
              <span style={{ fontSize: '17px' }}>
                You are on a 30-Day Free Trial
              </span>
              {billingStatus?.isFreeTrialProduct ? (
                <span className="secondary-text" style={{ color: gray300 }}>
                  You can explore our features for free during the trial period.
                  To continue enjoying full access without any interruptions, be
                  sure to subscribe to one of the available plans before your
                  trial ends.
                </span>
              ) : null}
              {billingStatus?.subscriptionStartDate &&
                billingStatus?.isFreeTrialProduct && (
                  <span className="secondary-text">
                    Your <strong>free trial</strong> started on{' '}
                    {monthUtilFormat(
                      new Date(billingStatus.subscriptionStartDate * 1000),
                      dateFormat,
                    )}
                  </span>
                )}
              <span className="secondary-text">
                Free trial expires on{' '}
                <span style={{ color: gray500 }}>
                  {monthUtilFormat(billingStatus.trialEnds, dateFormat)}{' '}
                  <span
                    style={{
                      fontWeight: 600,
                      color:
                        timeLeft.days <= 2 ? theme.errorText : 'inherit',
                    }}
                  >
                    ({countdownText})
                  </span>
                </span>
              </span>
            </div>
            {nextPaymentText}
            {paymentMethodText}
            {subscriptionWillBeCancelledText}
          </>
        );
      case 'active':
        return (
          <>
            {subsciptionPlanView}
            {financialInstitutionsUsageText}
            <div className="flex-column">
              {lastPaymentText}
              {nextPaymentText}
              {paymentMethodText}
              {subscriptionWillBeCancelledText}
            </div>
          </>
        );
      case 'past_due':
      case 'unpaid':
        return (
          <>
            {subsciptionPlanView}
            <div className="flex-column">
              <span style={{ fontSize: 13, color: theme.errorText }}>
                Your last payment failed.
              </span>
              {paymentMethodText}
              {showGoToBillingLinks && (
                <span>To change your payment method {gotToBillingLink}</span>
              )}
            </div>
          </>
        );
      case 'canceled':
        return (
          <>
            <span style={{ fontSize: 13, color: theme.warningTextLight }}>
              Your subscription to the <strong>{subscriptionPlan ? subscriptionPlan.name : 'Unknown'}</strong>{' '}
              plan has been canceled.
            </span>
            {freeVersionView}
          </>
        );
      case 'incomplete':
      case 'incomplete_expired':
      case 'paused':
      default:
        return freeVersionView;
    }
  };

  return <div className={styles.billingContainer}>{getBillingInfoView()}</div>;
};
export default BillingInfo;
